// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React from 'react';
import { Typography, Box, FormControl, Select, MenuItem, ListItemText, SelectChangeEvent } from '@mui/material';
import CustomerHealthToggle, { AccountScopeState } from './CustomerHealthToggle';
import { RiskIndicatorSelect } from './RiskIndicatorSelect';
import { useAuthContext } from '@asgardeo/auth-react';
import { RiskFilterKey } from '../data/utils/types';
import { RegionFilterSelect } from "./RegionFilterSelect";
import { ProductFilterSelect } from "./ProductFilterSelect";
import { ABTTeamFilterSelect } from "./ABTTeamFilterSelect";

export interface HealthFilters {
    accountScope: "my-accounts" | "all-accounts";
    userEmail: string | null;
    riskIndicators: RiskFilterKey[];
    region: string[];
    product: string | null;
    abtTeam: string | null;
    healthStatus: string | null;
}

const HealthStatusFilterSelect: React.FC<{
    selectedStatus: string | null;
    onStatusChange: (newStatus: string | null) => void;
}> = ({ selectedStatus, onStatusChange }) => {
    const handleChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        onStatusChange(value || null);
    };
    return (
        <FormControl sx={{ m: 1, width: 180 }} size="small">
            <Select
                value={selectedStatus || ''}
                onChange={handleChange}
                displayEmpty
                renderValue={(selected) => {
                    if (!selected) return "All Statuses";
                    return selected === 'at_risk' ? 'At Risk' : 'Healthy';
                }}
            >
                <MenuItem value="">
                    <ListItemText primary="All Statuses" />
                </MenuItem>
                <MenuItem value="at_risk">
                    <ListItemText primary="At Risk" />
                </MenuItem>
                <MenuItem value="healthy">
                    <ListItemText primary="Healthy" />
                </MenuItem>
            </Select>
        </FormControl>
    );
};
interface CustomerHealthFiltersProps {
    onFilterChange: (filters: HealthFilters) => void;
    currentFilters: HealthFilters;
}

export const CustomerHealthFilters: React.FC<CustomerHealthFiltersProps> = ({ onFilterChange, currentFilters }) => {
    //retrieve user email from auth context
    const { state: authState } = useAuthContext();
    const userEmail = authState.email || null;

    const handleAccountScopeToggleClick = (newState: AccountScopeState) => {
        onFilterChange({
            ...currentFilters,
            accountScope: newState,
            userEmail
        });
    };
    const handleRiskChange = (newRisks: RiskFilterKey[]) => {
        onFilterChange({
            ...currentFilters,
            riskIndicators: newRisks
        });
    };
    const handleRegionChange = (newRegions: string[]) => {
        onFilterChange({
            ...currentFilters,
            region: newRegions
        });
    };
    const handleProductChange = (newProduct: string | null) => {
        onFilterChange({
            ...currentFilters,
            product: newProduct
        });
    };
    const handleABTTeamChange = (newTeam: string | null) => {
        onFilterChange({
            ...currentFilters,
            abtTeam: newTeam
        });
    };
    const handleHealthStatusChange = (newStatus: string | null) => {
        onFilterChange({
            ...currentFilters,
            healthStatus: newStatus
        });
    };
    return (
        <Box sx={{ my: 2, p: 2, border: '1px solid #eee', borderRadius: '4px', display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="h6">Filters</Typography>
            {/* Renders the my/all accounts toggle by passing the active scope for display and the handler for clicks */}
            <CustomerHealthToggle
                page={currentFilters.accountScope || "all-accounts"}
                onSwitchClick={handleAccountScopeToggleClick}
            />
            <RiskIndicatorSelect
                selectedRisks={currentFilters.riskIndicators || []}
                onRiskChange={handleRiskChange}
            />
            <RegionFilterSelect
                selectedRegions={currentFilters.region || []}
                onRegionChange={handleRegionChange}
            />
            <ProductFilterSelect
                selectedProduct={currentFilters.product || null}
                onProductChange={handleProductChange}
            />
            <ABTTeamFilterSelect
                selectedTeam={currentFilters.abtTeam || null}
                onTeamChange={handleABTTeamChange}
            />
            <HealthStatusFilterSelect
                selectedStatus={currentFilters.healthStatus || null}
                onStatusChange={handleHealthStatusChange}
            />
        </Box>
    );
};
